import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/feature/VideoCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { searchVideos, sortVideos, type SortKey } from "@/lib/videoData";
import { categories } from "@/mocks/videos";

export default function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get("q") || "";

  const [keyword, setKeyword] = useState(q);
  const [sort, setSort] = useState<SortKey>("hot");

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  usePageMeta({
    title: q ? `"${q}" 的搜索结果 - 快手短视频平台` : "搜索 - 快手短视频平台",
    description: q
      ? `在快手搜索"${q}"，查看相关短视频内容、作者与标签，快速发现你感兴趣的视频。`
      : "在快手搜索短视频、作者与标签。",
    keywords: "快手搜索,短视频搜索,快手视频",
  });

  const results = useMemo(() => {
    const list = searchVideos(q);
    return sortVideos(list, sort);
  }, [q, sort]);

  const submit = () => {
    const text = keyword.trim();
    if (!text) return;
    navigate(`/search?q=${encodeURIComponent(text)}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 pt-16">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-600 transition-colors mb-4 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回上一页
          </button>

          <section className="rounded-xl border border-background-200 bg-background-100 p-5 md:p-7 mb-6">
            <h1 className="text-lg md:text-xl font-bold text-foreground-950 mb-4">
              {q ? (
                <>
                  搜索关键词：<span className="text-primary-600">“{q}”</span>
                </>
              ) : (
                "输入关键词开始搜索"
              )}
            </h1>
            <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submit();
                  }}
                  type="text"
                  placeholder="搜索视频标题、标签或作者昵称"
                  className="w-full h-11 pl-10 pr-4 rounded-full bg-background-50 border border-background-200 text-sm outline-none focus:border-primary-400 transition-colors"
                />
              </div>
              <button
                type="button"
                onClick={submit}
                className="h-11 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </div>
          </section>

          {q && (
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <p className="text-sm text-foreground-500">
                共找到 <span className="text-primary-600 font-medium">{results.length}</span> 个相关视频
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                <button
                  type="button"
                  onClick={() => setSort("hot")}
                  className={`px-3 py-1 rounded-full text-xs transition-colors cursor-pointer ${
                    sort === "hot" ? "bg-background-50 text-primary-600" : "text-foreground-500"
                  }`}
                >
                  按相关度
                </button>
                <button
                  type="button"
                  onClick={() => setSort("new")}
                  className={`px-3 py-1 rounded-full text-xs transition-colors cursor-pointer ${
                    sort === "new" ? "bg-background-50 text-primary-600" : "text-foreground-500"
                  }`}
                >
                  按发布时间
                </button>
              </div>
            </div>
          )}

          {!q ? (
            <div className="py-10">
              <p className="text-sm text-foreground-500 mb-4">热门分类入口</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="flex flex-col items-center gap-2 py-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 hover:-translate-y-1 transition-all"
                  >
                    <span className="w-10 h-10 rounded-full bg-primary-50 text-primary-500 flex items-center justify-center text-xl">
                      <i className={cat.icon}></i>
                    </span>
                    <span className="text-xs text-foreground-700">{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-3xl text-foreground-400 mb-4">
                <i className="ri-search-eye-line"></i>
              </span>
              <p className="text-lg font-medium text-foreground-800">
                没有找到与“{q}”相关的视频
              </p>
              <p className="text-sm text-foreground-500 mt-1 mb-6">
                换个关键词试试，或从下面的热门分类探索
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-2xl">
                {categories.slice(0, 4).map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="flex items-center justify-center gap-2 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                  >
                    <i className={cat.icon}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-product-shop>
              {results.map((video) => (
                <VideoCard key={video.id} video={video} showCategory />
              ))}
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}